/**
 * @unscoped — AI 회의록(AiMinutesPage) 의 i18n.
 *
 * task-i18n / calendar-i18n 패턴 일관:
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty(ko/en/ja) 는 i18n-completeness.test.ts 가 강제
 *   - params 치환 ({n}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  'minutes.title': { ko: 'AI 회의록', en: 'AI Meeting Minutes', ja: 'AI議事録' },
  // 대시보드
  'minutes.dash.overview': { ko: '개요', en: 'Overview', ja: '概要' },
  'minutes.dash.today': { ko: '오늘의 회의', en: "Today's meetings", ja: '今日の会議' },
  'minutes.dash.recent': { ko: '최근 회의록', en: 'Recent minutes', ja: '最近の議事録' },
  'minutes.dash.stat_total': { ko: '전체 회의록', en: 'Total minutes', ja: '議事録全体' },
  'minutes.dash.stat_today': { ko: '오늘 회의', en: 'Today', ja: '今日の会議' },
  'minutes.dash.stat_week': { ko: '이번 주', en: 'This week', ja: '今週' },
  'minutes.dash.stat_ready': { ko: '완료', en: 'Ready', ja: '完了' },
  'minutes.dash.today_empty': { ko: '오늘 예정·작성된 회의가 없습니다.', en: 'No meetings today.', ja: '今日の会議はありません。' },
  'minutes.dash.recent_empty': { ko: '아직 회의록이 없습니다. 새 회의록을 만들어 보세요.', en: 'No minutes yet.', ja: 'まだ議事録がありません。' },
  'minutes.dash.viewall': { ko: '전체 보기', en: 'View all', ja: 'すべて表示' },
  'minutes.dash.view_card': { ko: '카드 보기', en: 'Card view', ja: 'カード表示' },
  'minutes.dash.view_list': { ko: '목록 보기', en: 'List view', ja: 'リスト表示' },
  // 전체보기(페이징) 뷰
  'minutes.dash.today_all': { ko: '오늘의 회의 전체', en: "All today's meetings", ja: '今日の会議すべて' },
  'minutes.dash.recent_all': { ko: '최근 회의록 전체', en: 'All recent minutes', ja: '最近の議事録すべて' },
  'minutes.dash.back': { ko: '대시보드', en: 'Dashboard', ja: 'ダッシュボード' },
  'minutes.page.prev': { ko: '이전', en: 'Prev', ja: '前へ' },
  'minutes.page.next': { ko: '다음', en: 'Next', ja: '次へ' },
  'minutes.page.indicator': { ko: '{cur} / {total} 페이지', en: 'Page {cur} / {total}', ja: '{cur} / {total} ページ' },
  'minutes.subtitle': {
    ko: '회의 원문을 붙여넣으면 요약·결정사항·액션아이템으로 정리합니다.',
    en: 'Paste raw meeting notes and get a summary, decisions, and action items.',
    ja: '会議の原文を貼り付けると、要約・決定事項・アクションアイテムに整理します。',
  },
  'minutes.btn.new': { ko: '새 회의록', en: 'New minutes', ja: '新規議事録' },
  'minutes.btn.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'minutes.btn.generate': { ko: 'AI 정리', en: 'Generate', ja: 'AI整理' },
  'minutes.btn.generating': { ko: 'AI 정리 중…', en: 'Generating…', ja: 'AI整理中...' },
  'minutes.btn.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'minutes.btn.attach': { ko: '파일 첨부', en: 'Attach file', ja: 'ファイル添付' },
  'minutes.btn.view_saved': { ko: '저장된 항목 보기', en: 'View saved entry', ja: '保存された項目を表示' },

  // 검색 / 필터 / 통계
  'minutes.search.placeholder': { ko: '제목 검색', en: 'Search title', ja: 'タイトル検索' },
  'minutes.filter.all': { ko: '전체', en: 'All', ja: 'すべて' },
  'minutes.filter.ready': { ko: '완료', en: 'Ready', ja: '完了' },
  'minutes.filter.failed': { ko: '실패', en: 'Failed', ja: '失敗' },
  'minutes.count': { ko: '{n}건', en: '{n} items', ja: '{n}件' },
  'minutes.search.no_match': { ko: '검색 결과가 없습니다', en: 'No matches', ja: '一致なし' },

  'minutes.list.empty': {
    ko: '아직 회의록이 없습니다. 새 회의록을 만들어 보세요.',
    en: 'No minutes yet. Create your first one.',
    ja: 'まだ議事録がありません。新規作成してください。',
  },
  'minutes.list.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中...' },
  'minutes.list.created': { ko: '작성 {date}', en: 'Created {date}', ja: '作成 {date}' },
  'minutes.attendees_count': { ko: '참석 {n}명', en: '{n} attendees', ja: '参加{n}名' },

  // 생성 폼
  'minutes.field.title': { ko: '제목', en: 'Title', ja: 'タイトル' },
  'minutes.field.date': { ko: '회의 날짜', en: 'Meeting date', ja: '会議日' },
  'minutes.field.attendees': { ko: '참석자 (쉼표로 구분)', en: 'Attendees (comma-separated)', ja: '参加者 (カンマ区切り)' },
  'minutes.field.source': { ko: '회의 원문', en: 'Raw notes', ja: '会議原文' },
  'minutes.placeholder.title': { ko: '예: 주간 운영 회의', en: 'e.g. Weekly ops sync', ja: '例: 週次運営会議' },
  'minutes.placeholder.attendees': { ko: '예: 김민수, 이영희', en: 'e.g. Alice, Bob', ja: '例: 山田, 佐藤' },
  'minutes.placeholder.source': {
    ko: '회의 중 오간 내용을 그대로 붙여넣으세요. AI 가 핵심을 정리합니다.',
    en: 'Paste the raw discussion here. AI will structure the key points.',
    ja: '会議の内容をそのまま貼り付けてください。AIが要点を整理します。',
  },
  'minutes.create.hint': {
    ko: '원문이 길수록 더 정확하게 정리됩니다. 결정사항·담당자·기한이 드러나도록 적어 주세요.',
    en: 'Longer notes yield better structure. Include decisions, owners, and due dates.',
    ja: '原文が長いほど整理精度が上がります。決定・担当・期限が分かるように記載してください。',
  },
  'minutes.create.charcount': { ko: '{n} / {max}자', en: '{n} / {max}', ja: '{n} / {max}字' },
  'minutes.create.attach_hint': {
    ko: '텍스트 파일(.txt·.md·.csv)을 첨부하면 원문에 채워집니다.',
    en: 'Attach a text file (.txt/.md/.csv) to fill the notes.',
    ja: 'テキストファイル(.txt/.md/.csv)を添付すると原文に取り込みます。',
  },
  'minutes.create.source_capped': {
    ko: '원문이 길어 앞부분 약 10만 자만 사용했어요. (AI 정리 기준)',
    en: 'Notes were long — only the first ~100,000 characters are used.',
    ja: '原文が長いため先頭の約10万字のみ使用します。',
  },
  'minutes.create.ai_failed': {
    ko: 'AI 서버가 원문을 정리하지 못했어요. 위 ‘AI 정리’로 다시 시도하거나 원문을 줄여 보세요. (회의 원문은 저장됨)',
    en: 'The AI could not structure the notes. Retry with “Generate” above or shorten the notes. (Your raw notes are saved.)',
    ja: 'AIが原文を整理できませんでした。上の「AI整理」で再試行するか原文を短くしてください。(会議原文は保存済み)',
  },
  'minutes.create.ai_failed_reason': { ko: '사유: {msg}', en: 'Reason: {msg}', ja: '理由: {msg}' },

  // 상세 — 양식정리(doc1 회의록 표 + doc2 참석자 명단)
  'minutes.section.form': { ko: '양식정리', en: 'Formatted minutes', ja: '様式整理' },
  'minutes.form.doc_title': { ko: '회 의 록', en: 'MEETING MINUTES', ja: '議 事 録' },
  'minutes.form.no': { ko: '회의록 번호', en: 'Minutes No.', ja: '議事録番号' },
  'minutes.form.topic': { ko: '회의 주제', en: 'Topic', ja: '会議主題' },
  'minutes.form.datetime': { ko: '일시', en: 'Date/Time', ja: '日時' },
  'minutes.form.place': { ko: '장소', en: 'Location', ja: '場所' },
  'minutes.form.author': { ko: '작성자', en: 'Author', ja: '作成者' },
  'minutes.form.reviewer': { ko: '검토자', en: 'Reviewer', ja: '検討者' },
  'minutes.form.approver': { ko: '승인자', en: 'Approver', ja: '承認者' },
  'minutes.form.content': { ko: '내 용', en: 'Content', ja: '内 容' },
  'minutes.form.attendee_list': { ko: '회의 참석자 명단', en: 'Attendee list', ja: '会議参加者名簿' },
  'minutes.form.rowno': { ko: '번호', en: 'No.', ja: '番号' },
  'minutes.form.affiliation': { ko: '소속', en: 'Affiliation', ja: '所属' },
  'minutes.form.name': { ko: '성명', en: 'Name', ja: '氏名' },
  'minutes.form.position': { ko: '직급', en: 'Position', ja: '職級' },
  'minutes.form.signature': { ko: '서명', en: 'Signature', ja: '署名' },
  'minutes.form.no_attendees': { ko: '참석자 정보가 없습니다', en: 'No attendees recorded', ja: '参加者情報がありません' },
  'minutes.form.assignee': { ko: '담당', en: 'Assignee', ja: '担当' },
  'minutes.form.due': { ko: '기한', en: 'Due', ja: '期限' },
  'minutes.section.summary': { ko: '요약', en: 'Summary', ja: '要約' },
  'minutes.section.decisions': { ko: '결정사항', en: 'Decisions', ja: '決定事項' },
  'minutes.section.actions': { ko: '액션아이템', en: 'Action items', ja: 'アクションアイテム' },
  'minutes.section.source': { ko: '회의 원문', en: 'Raw notes', ja: '会議原文' },
  /*
   * ── 원문이 «없는» 회의록 (2026-09-22) ──
   * 음성에서 온 회의록은 `source_text` 가 NULL 이다 — ⛔ 「비어 있다」가 아니라
   * **「여기 있는 것이 아니다」**(마이그 0005 주석). 그래서 빈 상자를 그리지 않고 사실을 적는다.
   */
  'minutes.origin.voice': { ko: '녹음', en: 'Recording', ja: '録音' },
  'minutes.source.none': {
    ko: '회의 원문이 없습니다.',
    en: 'No raw notes.',
    ja: '会議原文がありません。',
  },
  'minutes.source.none_voice': {
    ko: '이 회의록은 녹음에서 만들어졌습니다 — 붙여넣은 원문이 없습니다.',
    en: 'This entry was made from a recording — there are no pasted raw notes.',
    ja: 'この議事録は録音から作られました — 貼り付けた原文はありません。',
  },
  /** ⚠ 모바일 시안과 **같은 말**이어야 한다 — 지우는 자리는 모바일이고 이 화면엔 없다. */
  'minutes.source.voice_kept': {
    ko: '녹음을 지워도 이 회의록은 남습니다. 회의록을 없애려면 이 화면에서 삭제해 주세요.',
    en: 'Deleting the recording does not delete this entry. Delete it here if you want it gone.',
    ja: '録音を削除してもこの議事録は残ります。議事録を消すにはこの画面で削除してください。',
  },
  'minutes.source.show': { ko: '원문 보기', en: 'Show raw notes', ja: '原文を表示' },
  'minutes.source.hide': { ko: '원문 접기', en: 'Hide raw notes', ja: '原文を隠す' },
  'minutes.detail.model': { ko: '모델 {name}', en: 'Model {name}', ja: 'モデル {name}' },
  /*
   * ⭐ 「요약」 탭의 ★ 빈 자리 (2026-10-08 사용자 결정 · 시안 5절). ⇒ en·ja 는 ★ 사용자 답 ⑨.
   *
   * ## ⛔ `minutes.detail.no_summary` 와 ★ 왜 ⭐ 둘인가 — ⇒ 「할 수 있나」가 다르다
   *
   *     no_summary    「요약이 없습니다.」      ⇐ ★ 사실 진술 — ⭐ 단추가 «없는» 자리에서 쓴다
   *     summary_empty 「요약을 진행해주세요.」  ⇐ ⭐ 시킴 — ★ 단추가 «있는» 자리에서만 쓴다
   *
   * ⇒ ⚠ 시안 5절이 그 규율을 적었다: 문구를 고를 때 세는 것은 「몇 곳인가」가 아니라
   *   ★ 「⭐ 그 말을 받는 사람이 ★ 지금 그 자리에서 ⭐ 그것을 할 수 «있나»」다.
   * ⇒ ⇒ ⛔ 그래서 ★ 직접 쓴 회의록(웹에 요약 길이 «없다»)에는 ⭐ 「진행해주세요」를 쓰지 않는다.
   *   ⇒ ★ 「전체」 탭 양식의 그 자리도 ⭐ `no_summary` 그대로다 — 거기엔 단추가 없다.
   *
   * ## ⚠ `summary_empty_why` 가 ★ 「액션아이템」이라 적는 까닭
   *
   * 시안 3절 ★ 그림은 ⭐ 「할 일」이라 적었다. ⇒ ⛔ 그런데 시안 5절 «열쇠 표»에 그 이름이 없어
   *   ★ 낱말을 바꾸기로 ⭐ 정한 적이 «없다». ⇒ ⇒ 그래서 ★ 절 제목(`minutes.section.actions`)과
   *   ⭐ **같은 말**을 쓴다. ⇒ ★ 그 물음은 ⭐ 사용자 결정 ⑫ 로 올라가 있다(관제) —
   *   ⇒ ⚠ 답이 오면 ★ 이 열쇠와 절 제목을 ⭐ «한 판»으로 옮긴다.
   */
  'minutes.detail.summary_empty': {
    ko: '요약을 진행해주세요.',
    en: 'Please run the summary.',
    ja: '要約を実行してください。',
  },
  'minutes.detail.summary_empty_why': {
    ko: '이 회의는 녹음과 받아쓴 글만 있습니다. 누르면 AI 가 요약·결정사항·액션아이템을 만듭니다.',
    en: 'This meeting has only the recording and its transcript. Running the summary creates the summary, decisions and action items.',
    ja: 'この会議には録音と文字起こしだけがあります。実行すると AI が要約・決定事項・アクションアイテムを作成します。',
  },
  'minutes.detail.summary_source_hint': {
    ko: '원문은 「전체」 탭에서 지금도 읽을 수 있습니다.',
    en: 'The transcript is already readable in the Full document tab.',
    ja: '原文は「全体」タブで今も読めます。',
  },
  /*
   * ⭐ 요약이 끝났을 때의 ★ «띠» (2026-10-08 사용자 결정 ⑦ · 시안 4절).
   *
   * ## ⚠⚠ ★ 이 글자는 ⭐ «확인 대기»다
   *
   * 사용자가 고른 것은 ★ **꼴**이다 — 「⭐ 저절로 채우지 않고 ★ 알린다」(까닭: 긴 회의록을
   * 읽는 중에 ⭐ 읽던 자리를 잃지 않게). ⇒ ⛔ **글자는 아직 정해지지 않았다.**
   * ⇒ ★ 보기 둘 ⇒ ⓐ 「요약이 끝났습니다.」 · ⓑ 「요약이 왔습니다.」
   * ⇒ ⇒ ⭐ 집 말투를 재서 ★ ⓐ 를 골라 두었다(시안 5절 끝 상자):
   *     ★ 「완료」는 이 레포에서 ⭐ «상태 이름»이고(값이 그 한 낱말인 것 12건) ⇒ 문장 동사로 ⛔ 0건
   *     ★ 「끝났」의 ⭐ 유일한 보기가 ★ 회의록 자신이다
   *     ★ 「왔습니다」는 이 레포에서 ⭐ 「가져왔습니다」로만 나오고 ⇒ 요약은 ★ «만들어진» 것이다
   * ⇒ ⇒ ⇒ ⚠ 사용자 답이 ⓑ 면 ★ **이 값만** 고치면 된다 — 열쇠·배선은 그대로다.
   */
  'minutes.detail.summary_ready': {
    ko: '요약이 끝났습니다.',
    en: 'The summary is ready.',
    ja: '要約が完了しました。',
  },
  'minutes.detail.summary_ready_view': { ko: '보기', en: 'View', ja: '表示' },
  'minutes.detail.no_summary': { ko: '요약이 없습니다.', en: 'No summary.', ja: '要約がありません。' },
  'minutes.detail.no_decisions': { ko: '추출된 결정사항이 없습니다.', en: 'No decisions extracted.', ja: '抽出された決定事項がありません。' },
  'minutes.detail.no_actions': { ko: '추출된 액션아이템이 없습니다.', en: 'No action items extracted.', ja: '抽出されたアクションアイテムがありません。' },
  'minutes.detail.failed_notice': {
    ko: 'AI 정리에 실패한 회의록입니다. 원문은 보존되어 있습니다.',
    en: 'AI structuring failed for this entry. The raw notes are preserved.',
    ja: 'AI整理に失敗した議事録です。原文は保存されています。',
  },
  'minutes.action.due': { ko: '기한 {v}', en: 'Due {v}', ja: '期限 {v}' },

  // 복사
  'minutes.copy': { ko: '복사', en: 'Copy', ja: 'コピー' },
  'minutes.copied': { ko: '복사됨', en: 'Copied', ja: 'コピー済み' },
  'minutes.copy_all': { ko: '전체 복사', en: 'Copy all', ja: '全文コピー' },

  // 상태
  'minutes.status.pending': { ko: '대기', en: 'Pending', ja: '保留' },
  'minutes.status.ready': { ko: '완료', en: 'Ready', ja: '完了' },
  'minutes.status.failed': { ko: '실패', en: 'Failed', ja: '失敗' },

  // 빈 상태
  'minutes.empty.title': { ko: '회의록을 선택하세요', en: 'Select a minutes entry', ja: '議事録を選択' },
  'minutes.empty.detail': {
    ko: '왼쪽 목록에서 회의록을 선택하거나 새 회의록을 만들어 보세요.',
    en: 'Pick an entry from the left, or create a new one.',
    ja: '左の一覧から選択するか、新規作成してください。',
  },
  'minutes.no_attendees': { ko: '참석자 없음', en: 'No attendees', ja: '参加者なし' },

  // 에러 / 확인
  'minutes.err.title_required': { ko: '제목을 입력하세요.', en: 'Please enter a title.', ja: 'タイトルを入力してください。' },
  'minutes.err.source_required': { ko: '회의 원문을 입력하세요.', en: 'Please enter the raw notes.', ja: '会議原文を入力してください。' },
  'minutes.err.file_empty': { ko: '빈 텍스트 파일이에요.', en: 'The text file is empty.', ja: '空のテキストファイルです。' },
  'minutes.err.file_read': { ko: '파일을 읽지 못했어요.', en: 'Could not read the file.', ja: 'ファイルを読み込めませんでした。' },
  'minutes.err.file_not_text': {
    ko: '텍스트 파일(.txt·.md·.csv)만 첨부할 수 있어요. (음성 파일 변환은 모바일 앱에서 지원)',
    en: 'Only text files (.txt/.md/.csv) can be attached. (Audio transcription is available in the mobile app.)',
    ja: 'テキストファイル(.txt/.md/.csv)のみ添付できます。(音声の文字起こしはモバイルアプリで対応)',
  },
  'minutes.err.generate_failed': { ko: 'AI 정리 실패: {msg}', en: 'Generation failed: {msg}', ja: 'AI整理失敗: {msg}' },
  'minutes.err.load_failed': { ko: '불러오기 실패', en: 'Failed to load', ja: '読み込み失敗' },
  'minutes.err.delete_failed': { ko: '삭제 실패', en: 'Delete failed', ja: '削除失敗' },
  'minutes.err.delete_forbidden': {
    ko: '본인이 작성한 회의록만 삭제할 수 있어요.',
    en: 'Only the author can delete this minutes entry.',
    ja: '本人が作成した議事録のみ削除できます。',
  },
  'minutes.confirm.delete': { ko: '이 회의록을 삭제할까요?', en: 'Delete this minutes entry?', ja: 'この議事録を削除しますか？' },

  // ══ 정리 대기 — 녹음은 끝났고 회의록은 아직 없는 세션 (2026-10-04 · F1-c) ══
  //
  // ⛔ 「정리 대기」와 위 `minutes.status.pending`(「대기」)는 **다른 것**이다:
  //    「대기」      = 회의록 행이 «있고» AI 정리가 안 끝난 것
  //    「정리 대기」 = 회의록 행이 «없는» 음성 세션(session.minutes_id === null)
  //   ⇒ ★ 글자를 가까이 두되 **뜻을 섞지 말 것** (`awaiting-summary.ts` 머리말).
  'minutes.tab.all': { ko: '전체', en: 'All', ja: 'すべて' },
  'minutes.tab.ready': { ko: '완료', en: 'Ready', ja: '完了' },
  'minutes.tab.awaiting': { ko: '정리 대기', en: 'Awaiting summary', ja: '整理待ち' },
  'minutes.tab.aria': { ko: '회의록 상태로 거르기', en: 'Filter minutes by state', ja: '議事録を状態で絞り込む' },
  /*
   * ⭐ 2026-10-10 (웹 ㉣) — 목록 «회의록 묶음» 머리글. 정리 대기 묶음은 탭 글자(minutes.tab.awaiting)를 그대로 쓴다
   *   (같은 뜻을 두 글자로 두지 않으려고). en·ja 는 이 파일의 말투(「Recent minutes」·「最近の議事録」)를 따랐다.
   */
  'minutes.group.minutes': { ko: '회의록', en: 'Minutes', ja: '議事録' },


  /*
   * ⭐ 상세의 ★ 탭 셋 — 2026-10-08 사용자 결정(시안 2절).
   *
   * ⛔⛔ ★ 「전체」를 ⭐ `minutes.tab.all` 과 «나눠» 둔 것은 ★ 일부러다.
   *   ```
   *   minutes.tab.all   ⇒ ★ 목록의 ⭐ «거르개» — 「회의록을 ★ 다 보여라」
   *   minutes.dtab.full ⇒ ★ 상세의 ⭐ «주 탭»   — 「이 회의록의 ★ 문서 전체」
   *   ```
   *   ⇒ ★ 한국말이 ⭐ 같은 「전체」라서 ★ 열쇠를 합치고 싶어지는데 ⇒ ⛔ 뜻이 ⭐ 다르다.
   *   ⇒ ⇒ ★ 합치면 ⇒ ⭐ 목록 거르개 낱말을 고칠 때 ★ 상세 탭 이름이 ⭐ 함께 바뀐다.
   * ⚠ 「요약」은 ★ `minutes.section.summary` 와 ⭐ 같은 낱말이다 — ★ 그것은 ⭐ 괜찮다:
   *   하나는 ★ «탭 이름»이고 하나는 ⭐ 「전체」 탭 «안의 절 제목»이라 ★ 따로 움직일 수 있다.
   */
  'minutes.dtab.info': { ko: '회의정보', en: 'Meeting info', ja: '会議情報' },
  'minutes.dtab.summary': { ko: '요약', en: 'Summary', ja: '要約' },
  /*
   * ⭐⭐ 2026-10-09 — ★ 「전체」라는 ⭐ «낱말이 ★ 다른 탭으로 옮겨 갔다» (사용자 결정 ⓑ).
   *
   * ## ⛔ 왜 — ★ 웹과 앱이 ⭐ 같은 낱말을 ★ 다른 뜻으로 썼다
   *
   *     종전 웹 「전체」  ⇒ ★ 회의록 «문서»(양식정리 ＋ 회의 내용)
   *     앱   「전체」     ⇒ ⭐ «시간별 전사»(녹음 조각의 글)
   * ⇒ ⇒ ★ 사용자가 ⭐ 「웹도 앱처럼 바꾼다」를 골랐다(ⓑ).
   *
   * ## ★ 그래서 ⇒ ⭐ 열쇠 «뜻»이 이렇게 갈린다
   *
   *     minutes.dtab.doc   「회의록」  ⇐ ⭐ 새 열쇠 — ★ 종전 「전체」가 담던 «문서»
   *     minutes.dtab.full  「전체」    ⇐ ⚠ 열쇠는 ★ 그대로인데 ⭐ «뜻이 바뀌었다»
   *                                     ⇒ 이제 ★ 시간별 전사 ＋ ⭐ 녹음 내려받기
   * ⇒ ⛔ 그래서 ★ 부품 이름도 ⭐ 함께 옮겼다 — `MinutesFullPanel` → `MinutesDocPanel`,
   *   ⇒ 새 `MinutesTranscriptPanel` 이 ★ `full` 탭을 받는다. ⇒ ⚠ 이름과 뜻을 ⭐ 함께 옮기지
   *     않으면 ★ 다음 사람이 ⭐ 「`full` 인데 문서?」에서 ★ 멈춘다.
   *
   * ## ⚠ en 이 ★ 바뀐다 — ⛔ ko 는 그대로 「전체」다
   *
   *   종전 en `'Full document'` 는 ★ «문서»를 가리켰다. ⇒ 지금 그 탭은 ⭐ 전사·녹음이라
   *   ⇒ ★ `'Full transcript'` 로 옮긴다. ⇒ ⚠ ja 는 ⭐ 「全体」 그대로다(뜻이 넓다).
   * ⚠ 앱이 ★ 이 «값»들을 ⭐ 맞댈 예정이다(관제) ⇒ ★ 그래서 ⭐ PR 본문에 값으로 적는다.
   */
  'minutes.dtab.doc': { ko: '회의록', en: 'Minutes', ja: '議事録' },
  'minutes.dtab.full': { ko: '전체', en: 'Full transcript', ja: '全体' },
  'minutes.dtab.aria': { ko: '회의록에서 무엇을 볼지 고르기', en: 'Choose what to view in the minutes', ja: '議事録で何を見るか選ぶ' },

  /*
   * ⭐ 「회의정보」 탭의 ★ 이름표. ⇒ ⚠ 양식정리(「전체」)의 `minutes.form.*` 과 ⭐ 쓰임이 다르다 —
   *   그쪽은 ★ «뽑아서 서명하는 문서»의 칸 이름이고, 이쪽은 ⭐ «읽는 자리»의 이름표다.
   * ⛔ 그리고 ★ 「소속」·「직급」 이름표를 ⭐ 짓지 «않았다» — ⇒ ★ 그 데이터가 ⭐ «없다».
   *   `MinutesDetail.attendees` 는 ★ `string[]`(이름뿐)이고, 양식정리 doc2 의 소속·직급·서명 칸은
   *   ⭐ 일부러 ★ «빈 칸»이다(뽑아서 손으로 적고 서명하는 자리다 — 떠 온 DOM 으로 확인했다).
   */
  /*
   * ⛔ 「회의명」·「날짜」를 ★ 양식(「전체」 탭)의 `minutes.form.*` 에서 ⭐ 빌리지 «않았다».
   *
   *     minutes.form.topic ⇒ ★ 「회의 주제」 — ⭐ 뽑아서 서명하는 «문서»의 칸 이름
   *     minutes.info.title ⇒ ★ 「회의명」   — ⭐ 읽는 자리의 이름표 (시안 2절이 그린 말)
   *
   *   ⇒ ⚠ 그리고 ★ 그 칸이 담는 값은 ⭐ `detail.title` 이다 — ★ 「주제」가 아니라 ⭐ «이름»이다.
   *   ⇒ ⛔ 그래도 ★ `minutes.form.topic` 의 값은 ⭐ 고치지 «않았다» — 그것은 ★ 양식의 낱말이고
   *     ⭐ 바꾸면 «뽑는 문서»가 바뀐다. ⇒ ★ 그 낱말은 ⭐ 사용자에게 물을 일로 남겼다.
   * ⚠ 날짜도 같다 — `minutes.field.date` 는 「회의 날짜」(만들 때의 폼 이름표)이고,
   *   여기는 이미 「회의정보」라 적힌 자리라 ⭐ 「날짜」로 족하다(시안도 그렇게 그렸다).
   */
  'minutes.info.title': { ko: '회의명', en: 'Meeting', ja: '会議名' },
  'minutes.info.date': { ko: '날짜', en: 'Date', ja: '日付' },
  /*
   * ⭐ 「전체」 탭 — ★ 시간별 전사 ＋ ⭐ 녹음 내려받기 (2026-10-09 · 사용자 결정 ⓑ).
   *
   * ## ⛔⛔ 「실패가 ★ 아닌 것」을 ⇒ ⭐ 실패라 말하지 «않는다»
   *
   * 서버 머리말(`GET …/transcript`)이 ★ 셋을 ⭐ 못 박았다:
   *     done ＋ 빈 글  ⇒ ⭐ 「조용한 5분」 — ★ «정상 결과»다
   *     device_side    ⇒ ⭐ 단말이 전사했다 ⇒ ★ 서버에 글이 «없다» — ⛔ 실패로 세지 않는다
   *     skipped        ⇒ ⭐ «말단»이다 — ★ 되살릴 것도 없다
   * ⇒ ★ 그래서 ⭐ 그 셋에 ★ «각자의 말»을 준다. ⇒ ⛔ 하나로 묶으면 ⭐ 거짓이 섞인다.
   * ⚠ 그리고 ★ `pending`·`running` 도 ⭐ «합치지 않는다» — 서버가 그 까닭을 적었다:
   *   「★ 「아직 줄 서 있다」와 ⭐ 「지금 돌고 있다」는 ★ 사용자에게 ⭐ 다른 말이고,
   *    ⇒ 합치면 ★ 앱이 ⭐ 되돌릴 수 없다」.
   *
   * ## ⚠ 녹음 ★ 네 꼴 (⇒ 서버 소스로 쟀다)
   *
   *     audio_ready true        ⇒ ★ 내려받기 ⭐ 단추
   *     null ＋ failed 아님      ⇒ ⭐ 「잇는 중」 — ⛔ 실패가 «아니다»
   *     session.status 'failed' ⇒ ★ 합본 실패(⇒ 상한 초과 또는 압축 실패 — ⚠ 서버가 둘을 ⭐ 안 가른다)
   *     'purged' · 줄 없음       ⇒ ⭐ 아무것도 안 그린다
   */
  'minutes.full.transcript': { ko: '시간별 전사', en: 'Transcript by time', ja: '時間別の文字起こし' },
  'minutes.full.empty': {
    ko: '아직 전사된 글이 없습니다.',
    en: 'No transcript yet.',
    ja: 'まだ文字起こしがありません。',
  },
  'minutes.full.partial': {
    ko: '아직 올라오는 중입니다 — 전사가 더 늘어날 수 있습니다.',
    en: 'Still arriving — more transcript may appear.',
    ja: 'まだ届いています — 文字起こしが増える可能性があります。',
  },
  'minutes.full.chunk_quiet': { ko: '조용한 구간입니다.', en: 'Quiet stretch.', ja: '静かな区間です。' },
  'minutes.full.chunk_device': {
    ko: '기기에서 전사해 서버에는 글이 없습니다.',
    en: 'Transcribed on the device — no text on the server.',
    ja: '端末で文字起こしされ、サーバーに文はありません。',
  },
  'minutes.full.chunk_failed': { ko: '이 구간은 전사하지 못했습니다.', en: 'This stretch failed.', ja: 'この区間は失敗しました。' },
  'minutes.full.chunk_skipped': { ko: '이 구간은 전사하지 않았습니다.', en: 'This stretch was skipped.', ja: 'この区間はスキップされました。' },
  'minutes.full.chunk_pending': { ko: '전사 대기', en: 'Waiting', ja: '待機中' },
  'minutes.full.chunk_running': { ko: '전사 중', en: 'Transcribing', ja: '文字起こし中' },
  'minutes.full.download': { ko: '녹음 내려받기', en: 'Download recording', ja: '録音をダウンロード' },
  'minutes.full.downloading': { ko: '받는 중…', en: 'Preparing…', ja: '準備中…' },
  'minutes.full.audio_joining': {
    ko: '녹음을 잇는 중입니다 — 끝나면 받을 수 있습니다.',
    en: 'The recording is still being joined.',
    ja: '録音を結合中です。',
  },
  'minutes.full.audio_failed': {
    ko: '녹음을 잇지 못했습니다 — 받을 수 없습니다.',
    en: 'The recording could not be joined.',
    ja: '録音を結合できませんでした。',
  },
  'minutes.full.download_failed': { ko: '녹음을 받지 못했습니다.', en: 'Download failed.', ja: 'ダウンロードに失敗しました。' },
  'minutes.info.state': { ko: '상태', en: 'State', ja: '状態' },
  'minutes.info.made': { ko: '만든 방법', en: 'Created from', ja: '作成方法' },
  'minutes.info.attendees': { ko: '참석자', en: 'Attendees', ja: '参加者' },
  'minutes.awaiting.badge': { ko: '정리 대기', en: 'Awaiting', ja: '整理待ち' },
  'minutes.awaiting.summarize': { ko: '요약하기', en: 'Summarize', ja: '要約する' },
  'minutes.awaiting.queued': { ko: '요약을 시작했습니다.', en: 'Summarization started.', ja: '要約を開始しました。' },
  // ⛔ 2026-10-20 사용자 「걷어라」 — 'minutes.awaiting.queued_hint'(「정리가 끝나면 이 목록에 …」)를 걷었다.
  //   목록 줄에서만 참인 말이었고 ④ 후 A 로 그 자리가 없어졌다(MinutesSummarizeSay 머리말).
  'minutes.awaiting.failed': { ko: '요약을 걸지 못했습니다.', en: 'Could not start summarization.', ja: '要約を開始できませんでした。' },
  // ⭐ ㉠ (2026-10-05 · 사용자 결정 ⓑ) — 단추를 «끄지 않고» 누르면 이 말을 한다.
  //   까닭: 단추를 끄면 ⛔ «왜 꺼졌는지 안 보인다»(관제 지적). ⇒ 누를 수 있게 두고 답한다.
  // ⚠ 그리고 ⛔ 그때 서버를 «부르지 않는다» — 부르면 워커가 조용히 물높이만 올리고 끝나서
  //   「눌렀는데 아무 일도 안 일어난다」가 된다(voice-worker.ts 의 text.trim()==='' 갈래).
  'minutes.awaiting.no_text': { ko: '요약할 글이 없습니다.', en: 'There is no text to summarize.', ja: '要約する文章がありません。' },
  // ⛔⛔ 까닭을 ★ 셋으로 가른다 (2026-10-06 · 관제 지적) — `transcript_chars === 0` 은 두 가지다:
  //   ⓐ 아직 전사가 «안 끝났다»(기다리면 생긴다) · ⓑ 끝났는데 «빈 말»(기다려도 안 생긴다)
  //   ⇒ ⛔ 한 문구로 덮으면 사용자가 ★ 「기다리면 되나 안 되나」를 ⭐ 못 가린다.
  //   ⇒ ★ 가르는 것은 `noTextReason`(awaiting-summary.ts) — 그 머리말에 «좁음»도 적혀 있다.
  'minutes.awaiting.no_text_arriving': {
    ko: '녹음 조각이 아직 올라오는 중입니다. 조금 뒤 다시 눌러 주세요.',
    en: 'Recording chunks are still uploading. Please try again in a moment.',
    ja: '録音チャンクがまだアップロード中です。少し後にもう一度押してください。',
  },
  'minutes.awaiting.no_text_working': {
    ko: '전사가 아직 남아 있습니다. 조금 뒤 다시 눌러 주세요.',
    en: 'Transcription is still in progress. Please try again in a moment.',
    ja: '文字起こしがまだ残っています。少し後にもう一度押してください。',
  },
  'minutes.awaiting.no_text_settled': {
    ko: '전사가 끝났지만 글자가 없습니다. 조용한 녹음이거나 전사가 글자를 못 만든 경우입니다.',
    en: 'Transcription finished but produced no text — the recording may be silent, or transcription found nothing.',
    ja: '文字起こしは終わりましたが文字がありません。無音か、文字起こしが何も生成しなかった場合です。',
  },
  /*
   * ⭐⭐ 2026-10-07 사용자 결정 ⓑ — 「실패한 조각이 있으면 누르는 «즉시» 말한다」.
   *
   * ⛔ **열쇠 이름을 `failed_chunks` 와 «확실히» 다르게 두었다.** 그것은 목록의 **칩 라벨**
   *    (「전사 실패 {n}」)이고 이것은 **누르면 하는 말**이다. ⇒ 같은 이름에 다른 뜻을 담으면
   *    어느 날 한쪽을 고치며 다른 쪽을 망친다(이 레포가 여러 번 겪은 병이다).
   *
   * ★ 꼴은 `no_text` 가족과 같다 — **말 ＋ 까닭** 두 줄이다. 앱(회의록 세션)이 이 열쇠로 찾아
   *   ko 값을 베끼므로, 그쪽도 같은 두 줄로 쓸 수 있게 갈라 둔다.
   *
   * ⚠ 둘째 줄 마지막 문장(「다시 전사할 수 없습니다」)은 **지어낸 말이 아니다** —
   *   `chat-server/src/modules/voice/voice.repo.ts:772~790` 이 2026-09-28 정정으로
   *   「`failed` 는 «끝»이다 · 되살리는 것은 `skipped` 뿐 · 다시 하게 하려면 그 길을 새로
   *   만들어야 한다 — 지금은 없다」고 적어 두었다(종전 주석 「다시 해 볼 수 있다」가 거짓이었고
   *   앱을 잘못 이끌었다). ⇒ ⛔ 그래서 「다시 전사」 같은 **없는 길을 내밀지 않는다.**
   *   ⇒ ★ **만료 조건**: 그 길이 생기면 이 둘째 줄을 그때 고친다.
   */
  'minutes.awaiting.cannot_finish': {
    ko: '요약이 끝까지 가지 못합니다.',
    en: 'Summarization cannot run to completion.',
    ja: '要約を最後まで進められません。',
  },
  'minutes.awaiting.cannot_finish_why': {
    ko: '전사가 실패한 조각이 {n}개 있어 그 자리에서 멈춥니다. 그 조각은 다시 전사할 수 없습니다.',
    en: 'It stops at {n} chunk(s) whose transcription failed. Those chunks cannot be transcribed again.',
    ja: '文字起こしに失敗したチャンクが {n} 個あり、そこで止まります。そのチャンクは再度文字起こしできません。',
  },
  /*
   * ⭐⭐ 2026-10-20 (사용자 결정 · 시안 ② · https://claude.ai/artifact/4ky1JHpLFNiR77ZpFZXyA6) — 서버가 녹음이 끝나면 실패
   *   조각만 다시 받아 적고(#2553) 그래도 안 되는 조각만 빼고 요약한다. ⇒ 위 cannot_finish 둘은 «옛 서버»(gave_up_chunks
   *   칸 없음)에서만 말한다. ko 글은 시안 그대로다. 앱(회의록 세션)이 이 열쇠의 ko 값을 베낀다.
   */
  'minutes.awaiting.retrying': {
    ko: '받아 적지 못한 조각 {n}개를 다시 받아 적고 있습니다. 끝나면 요약할 수 있습니다.',
    en: 'Re-transcribing {n} chunk(s) that could not be transcribed. You can summarize when it finishes.',
    ja: '書き起こせなかったチャンク {n} 個をもう一度書き起こしています。終わると要約できます。',
  },
  'minutes.awaiting.queued_skip': {
    ko: '받아 적지 못한 조각 {n}개는 빼고 요약합니다.',
    en: 'The {n} chunk(s) that could not be transcribed will be left out of the summary.',
    ja: '書き起こせなかったチャンク {n} 個は除いて要約します。',
  },
  /* ⭐ 2026-10-20 (사용자 결정 · 시안 ③) — 글자 0 이고 전사가 끝난 녹음. 상세 「요약」 탭은 이 말 하나만 두고 단추를 안 둔다. */
  'minutes.voice.empty': {
    ko: '내부에 녹음된 내역이 없습니다.',
    en: 'There is nothing recorded in this recording.',
    ja: 'この録音には記録された内容がありません。',
  },
  // ⚠ 파기된 세션은 404 다(마이그 212 의 가리기) — 「없다」로 말한다. 409 를 따로 두지 않는다.
  'minutes.awaiting.gone': { ko: '그 녹음을 찾을 수 없습니다.', en: 'That recording was not found.', ja: 'その録音が見つかりません。' },
  'minutes.awaiting.empty': { ko: '정리를 기다리는 녹음이 없습니다.', en: 'No recordings awaiting summary.', ja: '整理待ちの録音はありません。' },
  'minutes.awaiting.chunks': { ko: '조각 {done}/{total}', en: '{done}/{total} chunks', ja: 'チャンク {done}/{total}' },
  'minutes.awaiting.failed_chunks': { ko: '전사 실패 {n}', en: '{n} failed', ja: '文字起こし失敗 {n}' },
  'minutes.awaiting.no_engine': {
    ko: '요약 엔진이 꺼져 있어 지금은 정리되지 않습니다.',
    en: 'The summary engine is off, so this cannot be processed right now.',
    ja: '要約エンジンが停止しているため、今は処理できません。',
  },

  // ══ 보내기 (2026-09-22) ══
  // ⛔ 확인 문구(「정말 보낼까요」·「되돌릴 수 없습니다」)를 두지 않는다 — 사용자가 뺐다.
  // ⛔ 「전달됨」류의 말을 쓰지 않는다 — 이력은 「보냈다」이고 「닿았다」가 아니다.
  'minutes.send.open': { ko: '보내기', en: 'Send', ja: '送信' },
  'minutes.send.title': { ko: '보내기', en: 'Send', ja: '送信' },
  'minutes.send.back': { ko: '뒤로', en: 'Back', ja: '戻る' },
  'minutes.send.step_dest': { ko: '어디로 보낼지', en: 'Where to send', ja: 'どこへ送るか' },
  'minutes.send.step_who': { ko: '받는 사람', en: 'Recipients', ja: '受信者' },

  'minutes.send.dest_approval_t': { ko: '전자결재로 올리기', en: 'Submit for approval', ja: '電子決裁に上げる' },
  'minutes.send.dest_approval_s': {
    ko: '회의록이 첨부된 기안을 만듭니다. 결재선은 기안 화면에서 정합니다.',
    en: 'Creates a draft with the minutes attached. The approval line is set on the draft screen.',
    ja: '議事録を添付した起案を作成します。決裁ラインは起案画面で指定します。',
  },
  'minutes.send.dest_message_t': { ko: '메시지로 보내기', en: 'Send as a message', ja: 'メッセージで送る' },
  // ⛔ 「첨부로 보냅니다」가 아니다 — 요약·결정·액션이 «본문»으로 간다(2026-09-22 정정).
  'minutes.send.dest_message_s': {
    ko: '팀·조직원에게 요약·결정·액션을 본문으로 보냅니다.',
    en: 'Sends the summary, decisions, and action items as the message body.',
    ja: '要約・決定・アクションを本文としてチーム・組織のメンバーに送ります。',
  },
  'minutes.send.dest_mail_t': { ko: '메일로 보내기', en: 'Send by mail', ja: 'メールで送る' },
  'minutes.send.dest_mail_s': {
    ko: '외부 주소로도 보낼 수 있습니다.',
    en: 'External addresses are allowed.',
    ja: '社外アドレスにも送れます。',
  },

  'minutes.send.payload_label': { ko: '보내는 것', en: 'What is sent', ja: '送るもの' },
  'minutes.send.payload_body': { ko: '회의록 본문', en: 'The minutes text', ja: '議事録の本文' },
  'minutes.send.payload_only': { ko: '이것만', en: 'Only this', ja: 'これだけ' },
  'minutes.send.no_audio': {
    ko: '녹음 파일은 보내지 않습니다. 고를 수도 없습니다.',
    en: 'The recording is never sent — there is no option to include it.',
    ja: '録音ファイルは送りません。選ぶこともできません。',
  },
  'minutes.send.next': { ko: '받는 사람 고르기', en: 'Choose recipients', ja: '受信者を選ぶ' },

  'minutes.send.org_label': { ko: '조직도에서 고르기', en: 'Pick from the org chart', ja: '組織図から選ぶ' },
  'minutes.send.candidate': { ko: '참석자 후보', en: 'Attendee candidate', ja: '参加者候補' },
  'minutes.send.candidate_hint': {
    ko: '회의록에 적힌 이름과 맞은 사람입니다. 자동으로 고르지 않습니다 — 직접 켜 주세요.',
    en: 'Matched against the names written on the minutes. Nothing is selected automatically — turn it on yourself.',
    ja: '議事録に書かれた名前と一致した人です。自動選択はしません — ご自身で選んでください。',
  },
  'minutes.send.ambiguous': {
    ko: '같은 이름이 둘 이상이라 후보로 올리지 않았습니다: {names}',
    en: 'Not offered as candidates because the name is shared by more than one person: {names}',
    ja: '同名が複数いるため候補にしていません: {names}',
  },
  'minutes.send.unmatched': {
    ko: '조직도에서 찾지 못한 이름입니다: {names}',
    en: 'Not found in the org chart: {names}',
    ja: '組織図で見つからなかった名前です: {names}',
  },
  'minutes.send.pick_count': { ko: '{n}명 선택', en: '{n} selected', ja: '{n}名選択' },
  'minutes.send.pick_count_cand': {
    ko: '{n}명 선택 · 참석자 후보 {c}명(자동 선택 안 함)',
    en: '{n} selected · {c} attendee candidate(s), not selected automatically',
    ja: '{n}名選択 · 参加者候補{c}名(自動選択なし)',
  },
  'minutes.send.external_label': { ko: '외부 메일 주소', en: 'External mail addresses', ja: '社外メールアドレス' },
  'minutes.send.external_hint': {
    ko: '쉼표로 구분해 적으세요. 사내 사람은 위 조직도에서 고릅니다.',
    en: 'Separate them with commas. Internal people are picked from the org chart above.',
    ja: 'カンマで区切って入力してください。社内の人は上の組織図から選びます。',
  },
  'minutes.send.submit_message': { ko: '메시지로 보내기', en: 'Send message', ja: 'メッセージを送る' },
  'minutes.send.submit_mail': { ko: '메일로 보내기', en: 'Send mail', ja: 'メールを送る' },
  'minutes.send.submit_approval': { ko: '기안 만들기', en: 'Create draft', ja: '起案を作成' },
  'minutes.send.sending': { ko: '보내는 중…', en: 'Sending…', ja: '送信中...' },

  'minutes.send.not_ready': {
    ko: '서버가 아직 이것을 줄 줄 모릅니다',
    en: 'The server does not serve this yet',
    ja: 'サーバーがまだこれを返せません',
  },
  'minutes.send.not_ready_detail': {
    ko: '보내는 길이 서버에 아직 없습니다. 준비되면 이 자리가 저절로 열립니다.',
    en: 'The sending route does not exist on the server yet. This opens by itself once it does.',
    ja: '送信経路がサーバーにまだありません。用意できればこの場所が自動で開きます。',
  },
  /**
   * ★ 결재 갈래는 **넘어간다** — 이 화면이 대신 올리지 않는다(2026-09-22).
   *   ⛔ 종전의 `approval_not_ready`(「아직 준비되지 않았습니다」)를 대신한다. 그 키는 옛 판을
   *     되짚을 수 있게 남겨 둔다 — 지우면 이 문구가 왜 바뀌었는지가 사라진다.
   */
  'minutes.send.approval_handoff': {
    ko: '기안 화면으로 넘어갑니다. 제목과 본문이 채워지고, 결재선은 그 화면에서 고릅니다.',
    en: 'You will be taken to the draft screen with the title and body filled in. The approval line is chosen there.',
    ja: '起案画面へ移動します。タイトルと本文が入り、決裁ラインはその画面で選びます。',
  },
  'minutes.send.approval_not_ready': {
    ko: '기안 화면으로 넘기는 길이 아직 준비되지 않았습니다.',
    en: 'Handing this over to the draft screen is not ready yet.',
    ja: '起案画面へ渡す経路がまだ準備できていません。',
  },
  'minutes.send.err_no_recipient': {
    ko: '받는 사람을 한 명 이상 고르세요.',
    en: 'Choose at least one recipient.',
    ja: '受信者を1人以上選んでください。',
  },
  'minutes.send.err_bad_email': {
    ko: '메일 주소 형식이 아닙니다: {value}',
    en: 'Not a mail address: {value}',
    ja: 'メールアドレスの形式ではありません: {value}',
  },
  'minutes.send.err_failed': { ko: '보내지 못했습니다: {msg}', en: 'Could not send: {msg}', ja: '送信できませんでした: {msg}' },
  /*
   * ⛔ 「그 갈래가 아직 안 선다」는 **오류 문구가 아니다** — 일반 실패처럼 적으면 사람이
   *   «자기 입력이 틀렸나»를 의심한다. 서버는 멀쩡하고 그 길만 아직 없다.
   */
  'minutes.send.channel_not_ready': {
    ko: '이 갈래는 아직 준비 중입니다',
    en: 'This channel is not ready yet',
    ja: 'この経路はまだ準備中です',
  },
  'minutes.send.channel_not_ready_detail': {
    ko: '서버에 이 갈래로 보내는 길이 아직 없습니다. 고친 곳은 없으니 다른 갈래로 보내거나 잠시 뒤에 다시 해 주세요.',
    en: 'The server cannot send through this channel yet. Nothing is wrong on your side — use another channel or try again later.',
    ja: 'サーバーにこの経路で送る仕組みがまだありません。入力に問題はありません — 別の経路か、しばらく後にお試しください。',
  },
  /*
   * ⛔ 아래 넷은 «뜻이 달라» 따로 있다. 한 문구로 덮으면 사람이 엉뚱한 곳을 고치려 든다.
   *   not_found    없는·못 보는 회의록 (404 + code:'NOT_FOUND')
   *   unavailable  지금 «물어볼 수 없다» (503 + code:'MINUTES_UNAVAILABLE') — 나중엔 된다
   *   gateway      앞단(프록시·엣지)이 답했다 — 본문이 JSON 이 아니었다
   *   too_many     받는 사람 상한 — 보내기 «전»에 화면이 막는다
   */
  'minutes.send.err_not_found': {
    ko: '이 회의록을 찾을 수 없습니다. 지워졌거나 볼 수 없는 회의록입니다.',
    en: 'This minutes entry cannot be found — it was deleted, or you cannot view it.',
    ja: 'この議事録が見つかりません。削除されたか、閲覧できない議事録です。',
  },
  'minutes.send.err_unavailable': {
    ko: '지금은 회의록을 확인할 수 없습니다. 잠시 뒤 다시 해 주세요.',
    en: 'The minutes service cannot be reached right now. Please try again shortly.',
    ja: '今は議事録を確認できません。しばらく後にもう一度お試しください。',
  },
  'minutes.send.err_gateway': {
    ko: '서버가 아니라 중간 단계(프록시·엣지)가 답했습니다. 잠시 뒤 다시 해 주시고, 계속되면 배포 상태를 확인해야 합니다.',
    en: 'A proxy or edge answered instead of the server. Try again shortly; if it persists, the deployment needs checking.',
    ja: 'サーバーではなく中間層(プロキシ・エッジ)が応答しました。しばらく後に再試行し、続く場合は配備状態の確認が必要です。',
  },
  /*
   * ⛔⛔ 아래 셋을 «같은 말로 덮지 않는다» (2026-09-22 메일 갈래).
   *   needs_setup     사용자가 **고칠 수 있다** — SMTP 를 등록하면 된다
   *   send_failed     사용자가 **못 고친다** — 받는 메일 서버가 안 받았다
   *   body_too_large  **성격이 다르다** — Fastify 가 핸들러 «전»에 낸 413.
   *                   ⛔ 이 말을 다른 400 에 붙이면 사용자가 받는 사람·주소를 고치려 든다.
   */
  'minutes.send.err_needs_setup': {
    ko: '보내는 메일 계정이 없어 못 보냈습니다. {msg}',
    en: 'No sending mail account, so it was not sent. {msg}',
    ja: '送信用メールアカウントがないため送れませんでした。{msg}',
  },
  'minutes.send.err_send_failed': {
    ko: '받는 메일 서버가 받지 않았습니다. 설정 문제가 아니니 잠시 뒤 다시 해 주세요. {msg}',
    en: 'The receiving mail server refused it. This is not a settings problem — try again shortly. {msg}',
    ja: '受信側のメールサーバーが受け付けませんでした。設定の問題ではないので、しばらく後に再試行してください。{msg}',
  },
  'minutes.send.err_body_too_large': {
    ko: '보낼 본문이 너무 깁니다. 회의록 요약·결정·액션을 줄인 뒤 다시 보내 주세요. (받는 사람·주소 문제가 아닙니다)',
    en: 'The body is too large. Shorten the summary, decisions, and action items and send again. (Not a recipient or address problem.)',
    ja: '送る本文が長すぎます。要約・決定・アクションを短くしてから再送してください。(受信者・アドレスの問題ではありません)',
  },
  'minutes.send.err_too_many': {
    ko: '받는 사람이 {n}명입니다. 한 번에 {max}명까지 보낼 수 있습니다.',
    en: '{n} recipients selected. At most {max} can be sent at once.',
    ja: '受信者が{n}名です。一度に{max}名まで送れます。',
  },

  /*
   * ★★ 보냈지만 «이력에 안 남은» 경우 — 서버가 201 이되 `logged:false` 로 사실대로 알려 준다.
   *   되돌리기가 없어 이력이 유일한 되짚을 길인데 그 줄이 없다. ⛔ 「전달됨」류의 반대편이다.
   */
  /*
   * ── 보낸 «뒤» 성공을 말한다 (2026-09-22 운영 결함) ──
   *
   * ⛔ 「전달」·「도착」·「읽음」은 여전히 **금지**다 — 서버가 모르는 사실이다.
   * ★ 「보냈습니다」는 **서버가 아는 사실**이고(201 이 recipients·room_id·mail_id·logged 로 말해 준다),
   *   그것을 안 말하면 사람이 **한 번 더 누른다** → 방 둘 · 메일 두 통 · 되돌리기 없음.
   */
  'minutes.send.sent_message': {
    ko: '{n}명에게 보냈습니다.',
    en: 'Sent to {n} people.',
    ja: '{n}名に送信しました。',
  },
  'minutes.send.sent_mail': {
    ko: '{n}명에게 메일을 보냈습니다.',
    en: 'Mail sent to {n} people.',
    ja: '{n}名にメールを送信しました。',
  },
  /** ⛔ 서버가 수를 «안 줬을 때» — 수를 만들어 적지 않는다. */
  'minutes.send.sent_message_nocount': {
    ko: '메시지를 보냈습니다.',
    en: 'The message was sent.',
    ja: 'メッセージを送信しました。',
  },
  'minutes.send.sent_mail_nocount': {
    ko: '메일을 보냈습니다.',
    en: 'The mail was sent.',
    ja: 'メールを送信しました。',
  },
  /** ★ 서버가 준 손잡이 — 「도착」이 아니라 «만들어진 메일 문서»의 번호다. */
  'minutes.send.sent_mail_id': {
    ko: '보낸 메일 번호 {id}',
    en: 'Sent mail no. {id}',
    ja: '送信メール番号 {id}',
  },
  /*
   * ── 「이미 보냈습니다」 — 또 누르는 것을 막는 사실 한 줄 ──
   * ⛔ 되묻지 않는다(「정말 보내시겠습니까」·「되돌릴 수 없습니다」 금지 — 사용자 결정).
   *   ★ 같은 회의록을 두 번 보내는 것이 «정당한» 경우가 있어서(다른 사람·다른 갈래) 막지 않고 «적는다».
   */
  'minutes.send.already_head': {
    ko: '이 회의록은 이미 {n}번 보냈습니다.',
    en: 'This entry has already been sent {n} time(s).',
    ja: 'この議事録はすでに {n} 回送信しました。',
  },
  'minutes.send.already_last': {
    ko: '마지막: {channel} · {when}',
    en: 'Last: {channel} · {when}',
    ja: '最後: {channel} · {when}',
  },
  'minutes.send.not_logged': {
    ko: '보냈습니다 — 다만 보낸 이력에 남지 않았습니다. 누구에게 갔는지 이 화면에서 되짚을 수 없습니다.',
    en: 'Sent — but it was not recorded in the send history, so you cannot trace the recipients here.',
    ja: '送信しました — ただし送信履歴に残っていません。誰に送ったかをこの画面で辿れません。',
  },
  'minutes.send.sent_truncated': {
    ko: '본문이 길어 줄여 보냈습니다. 잘린 뒷부분은 받는 사람이 볼 수 없습니다.',
    en: 'The body was shortened before sending. Recipients cannot see the cut part.',
    ja: '本文が長いため短縮して送信しました。切れた部分は受信者には見えません。',
  },

  /** 서버가 «다시 확인»해서 거절한 것 — 사유는 서버 글자를 그대로 싣는다. */
  'minutes.send.refused': {
    ko: '서버가 보내기를 거절했습니다: {msg}',
    en: 'The server refused to send: {msg}',
    ja: 'サーバーが送信を拒否しました: {msg}',
  },
  'minutes.send.sent': { ko: '보냈습니다', en: 'Sent', ja: '送信しました' },

  // 보낸 이력 — 「보냈다」를 적는 자리다.
  'minutes.send.history': { ko: '보낸 이력', en: 'Send history', ja: '送信履歴' },
  'minutes.send.history_empty': { ko: '아직 보낸 적이 없습니다.', en: 'Not sent yet.', ja: 'まだ送信していません。' },
  'minutes.send.history_not_ready': {
    ko: '보낸 이력을 서버가 아직 줄 줄 모릅니다.',
    en: 'The server does not serve the send history yet.',
    ja: '送信履歴をサーバーがまだ返せません。',
  },
  'minutes.send.history_failed': { ko: '보낸 이력을 불러오지 못했습니다.', en: 'Could not load the send history.', ja: '送信履歴を読み込めませんでした。' },
  /** ⛔ `history_not_ready`(기다리면 된다)와 «다른 말»이다 — 그 회의록이 없다는 뜻이다. */
  'minutes.send.history_gone': {
    ko: '이 회의록을 찾을 수 없어 이력을 볼 수 없습니다. 지워졌거나 볼 수 없는 회의록입니다.',
    en: 'The history cannot be shown — this minutes entry was deleted, or you cannot view it.',
    ja: 'この議事録が見つからないため履歴を表示できません。削除されたか、閲覧できない議事録です。',
  },
  'minutes.send.ch_approval': { ko: '결재', en: 'Approval', ja: '決裁' },
  'minutes.send.ch_message': { ko: '메시지', en: 'Message', ja: 'メッセージ' },
  'minutes.send.ch_mail': { ko: '메일', en: 'Mail', ja: 'メール' },
  'minutes.send.history_expand': { ko: '받는 사람 전원 보기', en: 'Show every recipient', ja: '受信者全員を表示' },
  'minutes.send.history_collapse': { ko: '접기', en: 'Collapse', ja: '折りたたむ' },
  'minutes.send.history_no_recipient': { ko: '받는 사람 기록이 없습니다', en: 'No recipients recorded', ja: '受信者の記録がありません' },
  /** 이력의 `kind:'room'` 줄 — 단체방으로 보낸 것. ⛔ 「기록 없음」으로 그리면 안 된다. */
  'minutes.send.room_label': { ko: '대화방', en: 'Chat room', ja: 'ルーム' },
  /** 서버가 표시 이름을 못 실은 사내 줄 — 버리지 않고 id 로라도 남긴다. */
  'minutes.send.unnamed_user': { ko: '이름 미기록 (id {id})', en: 'Name not recorded (id {id})', ja: '氏名未記録 (id {id})' },

  // ── 외부인 넣기 (2026-09-22 사용자 결정) ── 메일 갈래에만 있다.
  'minutes.send.external_placeholder': { ko: 'name@example.com', en: 'name@example.com', ja: 'name@example.com' },
  'minutes.send.external_add': { ko: '넣기', en: 'Add', ja: '追加' },
  'minutes.send.from_contacts': { ko: '명함첩', en: 'Address book', ja: 'アドレス帳' },
  'minutes.send.from_book': { ko: '명함첩에서 고르기', en: 'Pick from the address book', ja: 'アドレス帳から選ぶ' },
  'minutes.send.book_search': { ko: '이름·주소로 찾기', en: 'Search by name or address', ja: '名前・アドレスで検索' },
  'minutes.send.book_empty': { ko: '명함첩에 맞는 사람이 없습니다.', en: 'No matching contacts.', ja: '該当する連絡先がありません。' },
  'minutes.send.book_noname': { ko: '(이름없음)', en: '(no name)', ja: '(名前なし)' },
  'minutes.send.book_shared': { ko: '공유', en: 'Shared', ja: '共有' },
  'minutes.send.book_personal': { ko: '개인', en: 'Personal', ja: '個人' },
  'minutes.send.book_pick': { ko: '{n}명 담기', en: 'Add {n}', ja: '{n}名を追加' },
  'minutes.send.remove_recipient': { ko: '{value} 빼기', en: 'Remove {value}', ja: '{value} を外す' },
  'minutes.send.pick_external': { ko: '외부 {n}곳', en: '{n} external', ja: '社外{n}件' },
  // ⛔ 「보내지 못합니다」가 아니다 — 막아 둔 것은 «오는» 메일을 스팸으로 분류하는 규칙이다.
  'minutes.send.blocked_badge': { ko: '스팸 분류함', en: 'Spam-filtered', ja: '迷惑メール振分' },
  'minutes.send.blocked_hint': {
    ko: '「스팸 분류함」은 그 주소에서 «오는» 메일을 스팸으로 옮기는 내 설정입니다. 보내는 것은 막지 않습니다.',
    en: '“Spam-filtered” is your own rule for mail coming from that address. It does not block sending.',
    ja: '「迷惑メール振分」はその宛先から«届く»メールに対する自分の設定です。送信は妨げません。',
  },

  // ── 첨부·대화방·미리보기 ──
  'minutes.send.attach_not_sent': {
    ko: '첨부 {n}개(사진·녹음)는 함께 가지 않습니다.',
    en: 'The {n} attachment(s) (photos, recordings) are not included.',
    ja: '添付{n}件(写真・録音)は一緒に送られません。',
  },
  'minutes.send.room_notice': {
    ko: '받는 사람 {n}명이 든 대화방이 새로 만들어집니다. 그 방은 보낸 뒤에도 남고, 받는 사람끼리 서로를 봅니다.',
    en: 'A new group room with {n} recipient(s) is created. It stays after sending, and the recipients can see each other.',
    ja: '受信者{n}名が入った新しいルームが作られます。送信後も残り、受信者同士が互いを見られます。',
  },
  'minutes.send.preview_show': { ko: '보낼 내용 보기', en: 'Show what will be sent', ja: '送る内容を表示' },
  'minutes.send.preview_hide': { ko: '보낼 내용 접기', en: 'Hide what will be sent', ja: '送る内容を隠す' },
  // ⛔ 링크를 넣지 않는다 — 회의록 본문은 작성자만 본다(2026-09-22 사용자 결정). 사실을 적어 둔다.
  'minutes.send.payload_nolink': {
    ko: '회의록을 여는 링크도 넣지 않습니다 — 받는 사람은 보낸 글만 봅니다.',
    en: 'No link to the minutes is included — recipients see only the text that was sent.',
    ja: '議事録を開くリンクも入れません — 受信者は送られた文だけを見ます。',
  },
  'minutes.send.will_truncate': {
    ko: '보낼 내용이 {full}자로 상한({max}자)을 넘어 줄여서 보냅니다. 잘린 뒷부분은 받는 사람이 볼 수 없습니다.',
    en: 'The text is {full} characters, over the {max} limit, so it will be shortened. Recipients cannot see the cut part.',
    ja: '本文が{full}字で上限({max}字)を超えるため短縮して送ります。切れた部分は受信者には見えません。',
  },
  'minutes.send.preview_length': { ko: '({n}자)', en: '({n} characters)', ja: '({n}字)' },
  'minutes.send.preview_readonly': {
    ko: '여기 보이는 글이 그대로 갑니다. 이 화면에서는 고치지 않습니다.',
    en: 'Exactly this text is sent. It is not editable here.',
    ja: 'ここに見える文がそのまま送られます。この画面では編集しません。',
  },
};

export type MinutesI18nKey = keyof typeof MESSAGES;

export function tMinutes(key: MinutesI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

export function getMinutesLang(): Lang { return getSharedLang(); }
export function setMinutesLang(value: Lang): void { setSharedLang(value); }
export function subscribeMinutesLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
